import { AnimatePresence, motion } from "framer-motion"
import type { ReactNode } from "react"
import { useEffect, useRef } from "react"

type DrawerSide = "left" | "right" | "bottom"

const FOCUSABLE_SELECTORS =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

export type DrawerProps = {
  open: boolean
  onClose?: () => void
  title?: ReactNode
  side?: DrawerSide
  children: ReactNode
  className?: string
}

export function Drawer({ open, onClose, title, children, side = "left", className }: DrawerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    previouslyFocused.current = document.activeElement as HTMLElement | null

    const node = containerRef.current
    if (!node) {
      return
    }
    const drawerNode = node

    const focusFirstElement = () => {
      const focusables = Array.from(drawerNode.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS))
      if (focusables.length > 0) {
        focusables[0].focus()
      } else {
        drawerNode.focus()
      }
    }

    const raf = requestAnimationFrame(focusFirstElement)

    function handleKeydown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault()
        onClose?.()
        return
      }

      if (event.key !== "Tab") {
        return
      }

      const focusables = Array.from(drawerNode.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)).filter(
        (element) => element.offsetParent !== null || element === drawerNode,
      )

      if (focusables.length === 0) {
        event.preventDefault()
        drawerNode.focus()
        return
      }

      const first = focusables[0]
      const last = focusables[focusables.length - 1]

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    }

    document.addEventListener("keydown", handleKeydown)

    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener("keydown", handleKeydown)
      previouslyFocused.current?.focus({ preventScroll: true })
    }
  }, [open, onClose])

  const slideVariants = {
    left: { initial: { x: "-100%" }, animate: { x: 0 }, exit: { x: "-100%" } },
    right: { initial: { x: "100%" }, animate: { x: 0 }, exit: { x: "100%" } },
    bottom: { initial: { y: "100%" }, animate: { y: 0 }, exit: { y: "100%" } },
  }

  const variant = slideVariants[side]
  const overlayClass = [
    "drawer-overlay",
    side === "right" ? "drawer-overlay--right" : "",
    side === "bottom" ? "drawer-overlay--bottom" : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className={overlayClass}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <button type="button" className="drawer-backdrop" aria-hidden onClick={onClose} />
          <motion.div
            {...variant}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === "string" ? title : undefined}
            className={`drawer-panel ${className ?? ""}`.trim()}
            ref={containerRef}
            tabIndex={-1}
            data-side={side}
          >
            {title ? (
              <header className="drawer-header">
                <span className="drawer-title">{title}</span>
                <button type="button" className="drawer-close" onClick={onClose}>
                  Close
                </button>
              </header>
            ) : null}
            <div className="drawer-content">{children}</div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

export default Drawer
